import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./doc.css";
import { useScrollReveal } from "../utils/useScrollReveal";
import { ajax } from "../utils/request";

export default function Documentation({ isLoggedIn, setLoginOpen, setAccountOpen, setFaqOpen }) {
  const { t, i18n } = useTranslation();
  
  // State
  const [selectedCodeType, setSelectedCodeType] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [documentationData, setDocumentationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll reveal hooks
  const headerSection = useScrollReveal({ threshold: 0.05 });
  const filterSection = useScrollReveal({ threshold: 0.08 });
  const cardsSection = useScrollReveal({ threshold: 0.08 });

  // Fetch documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await ajax.get("/library/documents");
        if (response.data.code === 0 && response.data.data) {
          const documents = response.data.data;
          
          const mappedDocuments = documents.map(doc => ({
            id: doc.id,
            document_id: doc.document_id,
            title: doc.title,
            description: `${doc.title} - ${doc.discipline} - ${doc.jurisdiction}`,
            discipline: doc.discipline,
            jurisdiction: doc.jurisdiction,
            gcs_link: doc.gcs_link,
            date_created: doc.date_created
          }));
          
          setDocumentationData(mappedDocuments);
        }
      } catch (err) {
        console.error("Error fetching documents:", err);
        setError("Failed to load documents");
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const availableDisciplines = useMemo(() => {
    const relevantDocs = selectedState 
      ? documentationData.filter(d => d.jurisdiction === selectedState)
      : documentationData;
    return [...new Set(relevantDocs.map(doc => doc.discipline))].sort();
  }, [documentationData, selectedState]);

  const availableJurisdictions = useMemo(() => {
    const relevantDocs = selectedCodeType 
      ? documentationData.filter(d => d.discipline === selectedCodeType)
      : documentationData;
    return [...new Set(relevantDocs.map(doc => doc.jurisdiction))].sort();
  }, [documentationData, selectedCodeType]);

  const filteredDocs = documentationData.filter((doc) => {
    const matchesDiscipline = !selectedCodeType || doc.discipline === selectedCodeType;
    const matchesJurisdiction = !selectedState || doc.jurisdiction === selectedState;
    const matchesSearch = !searchQuery || 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesDiscipline && matchesJurisdiction && matchesSearch;
  });

  const resetFilters = () => {
    setSelectedCodeType("");
    setSelectedState("");
    setSearchQuery("");
  };

  const changeLanguage = () => {
    if (i18n.language && i18n.language === "en") {
      i18n.changeLanguage("fr");
    } else {
      i18n.changeLanguage("en");
    }
  };

  const clickLogin = () => {
    setLoginOpen(true);
  };

  return (
    <div className="documentation-page w-full flex flex-col items-center bg-[#f5f5f7] min-h-[100vh]">
      
      <div className="w-full max-w-[1200px] px-6 py-12 md:py-16 space-y-8">

        {/* HEADER SECTION */}
        <section 
          ref={headerSection.ref}
          className={`text-center space-y-3 scroll-reveal ${headerSection.isVisible ? 'visible' : ''}`}
        >
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900">
            {t("documentation.title")}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
            {t("documentation.subtitle")}
          </p>
        </section>

        {/* FILTER SECTION */}
        <section 
          ref={filterSection.ref}
          className={`bg-white shadow-lg rounded-2xl p-6 scroll-reveal scroll-reveal-delay-1 ${filterSection.isVisible ? 'visible' : ''}`}
        >
          <div className="flex flex-col md:flex-row gap-4 md:gap-6">
            
            {/* Search Bar */}
            <div className="flex-[2]">
              <label htmlFor="searchQuery" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("documentation.search.label")}
              </label>
              <div className="relative">
                <input
                  id="searchQuery"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("documentation.search.placeholder")}
                  className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1677ff] focus:border-transparent transition-all duration-200 bg-white text-gray-700"
                />
                <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Jurisdiction Filter */}
            <div className="flex-1">
              <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("documentation.filters.state")}
              </label>
              <select
                id="state"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20CADE] focus:border-transparent bg-white text-gray-700 cursor-pointer">
                <option value="">{t("documentation.filters.all")}</option>
                {availableJurisdictions.map(jurisdiction => (
                  <option key={jurisdiction} value={jurisdiction}>{jurisdiction}</option>
                ))}
              </select>
            </div>
            
            {/* Discipline Filter */}
            <div className="flex-1">
              <label htmlFor="codeType" className="block text-sm font-semibold text-gray-700 mb-2">
                {t("documentation.filters.codeType")}
              </label>
              <select
                id="codeType"
                value={selectedCodeType}
                onChange={(e) => setSelectedCodeType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#56267B] focus:border-transparent bg-white text-gray-700 cursor-pointer">
                <option value="">{t("documentation.filters.all")}</option>
                {availableDisciplines.map(discipline => (
                  <option key={discipline} value={discipline}>{discipline}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results count & Reset Button */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-gray-500">
              {t("documentation.resultsCount", { count: filteredDocs.length })}
            </span>
            {(selectedCodeType || selectedState || searchQuery) && (
              <span 
                onClick={() => { setSelectedCodeType(""); setSelectedState(""); setSearchQuery(""); }}
                className="text-[var(--blue-text)] font-medium hover:underline cursor-pointer"
              >
                {t("documentation.filters.reset") || "Clear all filters"}
              </span>
            )}
          </div>
        </section>

        {/* CARDS SECTION */}
        <section 
          ref={cardsSection.ref}
          className={`scroll-reveal scroll-reveal-delay-2 ${cardsSection.isVisible ? 'visible' : ''}`}
        >
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block p-8 bg-white rounded-2xl shadow-lg">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#56267B] mx-auto mb-4"></div>
                <p className="text-gray-600 text-lg font-medium">{t("documentation.loading") || "Loading..."}</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600 bg-white rounded-2xl shadow-lg p-8">
              {error}
            </div>
          ) : filteredDocs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDocs.map((doc) => (
                <div key={doc.id} className="doc-card bg-white shadow-lg rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{doc.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{doc.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
              <p className="text-gray-600 text-lg font-medium">{t("documentation.noResults")}</p>
            </div>
          )}
        </section>

        {/* CiviDocs Link Section */}
        <div className="text-center py-6">
          <p className="text-gray-700 text-base">
            {t("documentation.civiDocsMessage")}
            <span
              onClick={() => window.location.href = '/pdf/start'}
              className="ml-1 text-[var(--blue-text)] font-medium hover:underline cursor-pointer"
            >
              {t("documentation.civiDocsLink")}
            </span>
          </p>
        </div>

        {/* FOOTER SECTION */}
        <footer className="w-full mt-auto py-8 cursor-pointer flex flex-col items-center justify-center text-[14px]">
          <div className="flex flex-wrap items-center justify-center text-gray-500">
            {isLoggedIn ? (
              <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={() => setAccountOpen(true)}>{t("home.footer_text1")}</a>
            ) : (
              <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={clickLogin}>{t("home.footer_text2")}</a>
            )}
            <span className="text-gray-400 px-1">|</span>
            <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={() => setFaqOpen(true)}> {t("home.faq")} </a>

            <span className="text-gray-400 px-1">|</span>
            <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={() => window.open("https://www.linkedin.com/company/civibot")}>
              {t("home.footer_text4")}
            </a>
            <span className="text-gray-400 px-1">|</span>
            <a className="item px-[6px] hover:text-[var(--blue-text)]" onClick={changeLanguage}> {t("selectLang")} </a>
          </div>
          <div className="footer-desc w-full text-center text-gray-500 text-[14px] mt-3">
            {t("footer")}
          </div>
        </footer>

      </div>
    </div>
  );
}